/* Gift packaging stays a separate Shopify line. Preserve the native personalizer and its properties. */
(() => {
  'use strict';
  function baseVariant(id, variants) {
    const chosen = variants.find(v => String(v.id) === String(id));
    if (!chosen?.options?.includes('With Gift Box')) return id;
    return variants.find(v => v.options?.includes('No Gift Box') && chosen.options.every((value, index) => value === 'With Gift Box' || value === v.options[index]))?.id || id;
  }
  function prepare(body, config, selection) {
    // Editing replaces an existing personalized line; its gift boxes already exist.
    if (selection.editing) return { body, single: false, changed: false };
    const known = id => config.variants.some(v => String(v.id) === String(id));
    const addon = main => ({ id: config.giftVariantId, quantity: selection.quantity, properties: {
      'For': config.title, '_nuzzle_parent_variant': String(main.id),
      ...(main.properties?._customization_id ? { '_nuzzle_parent_customization': main.properties._customization_id } : {})
    } });
    const selected = selection.checked && config.available && config.giftVariantId;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const next = body instanceof FormData ? new FormData() : new URLSearchParams();
      for (const [key, value] of body.entries()) next.append(key, value);
      const single = next.has('id');
      const entries = single ? [['id', next.get('id')]] : [...next.entries()].filter(([key]) => /^items\[\d+\]\[id\]$/.test(key));
      const match = entries.find(([, id]) => known(id));
      if (!match) return { body, single: false, changed: false };
      const [idKey, oldId] = match;
      const newId = baseVariant(oldId, config.variants);
      next.set(idKey, String(newId));
      if (!selected) return { body: next, single: false, changed: String(oldId) !== String(newId) };
      // Reusing an already-built request must never append another gift line.
      if ([...next.entries()].some(([key, value]) => /^items\[\d+\]\[id\]$/.test(key) && String(value) === String(config.giftVariantId))) return { body: next, single: false, changed: true };
      let parentPrefix = single ? '' : idKey.replace(/\[id\]$/, '');
      const customization = next.get(single ? 'properties[_customization_id]' : `${parentPrefix}[properties][_customization_id]`);
      if (single) {
        for (const [key, value] of [...next.entries()]) {
          if (['id', 'quantity', 'selling_plan'].includes(key)) {
            next.delete(key); next.set(`items[0][${key}]`, value);
          } else if (key.startsWith('properties[')) {
            next.delete(key); next.append(`items[0][properties]${key.slice(10)}`, value);
          }
        }
        parentPrefix = 'items[0]';
      }
      const indexes = [...next.keys()].map(key => /^items\[(\d+)\]/.exec(key)).filter(Boolean).map(m => Number(m[1]));
      const index = Math.max(-1, ...indexes) + 1;
      const gift = addon({ id: newId, properties: { _customization_id: customization } });
      next.set(`${parentPrefix}[properties][_nuzzle_gift_box_quantity]`, String(selection.quantity));
      next.set(`items[${index}][id]`, String(gift.id));
      next.set(`items[${index}][quantity]`, String(gift.quantity));
      for (const [key, value] of Object.entries(gift.properties)) next.set(`items[${index}][properties][${key}]`, value);
      return { body: next, single, changed: true };
    }
    if (typeof body !== 'string') return { body, single: false, changed: false };
    let value;
    try { value = JSON.parse(body); } catch { return { body, single: false, changed: false }; }
    if (!value || typeof value !== 'object') return { body, single: false, changed: false };
    const single = !Array.isArray(value.items) && known(value.id);
    const items = single ? [value] : value.items;
    if (!Array.isArray(items)) return { body, single: false, changed: false };
    const main = items.find(item => known(item.id));
    if (!main) return { body, single: false, changed: false };
    const oldId = main.id;
    main.id = baseVariant(main.id, config.variants);
    if (!selected) return { body: JSON.stringify(value), single: false, changed: String(main.id) !== String(oldId) };
    if (items.some(item => String(item.id) === String(config.giftVariantId))) return { body: JSON.stringify(value), single: false, changed: true };
    main.properties = { ...main.properties, _nuzzle_gift_box_quantity: String(selection.quantity) };
    let result = value;
    if (single) {
      const { sections, sections_url, ...line } = value;
      result = { items: [line], ...(sections !== undefined ? { sections } : {}), ...(sections_url !== undefined ? { sections_url } : {}) };
    }
    result.items.push(addon(main));
    return { body: JSON.stringify(result), single, changed: true };
  }
  // The same pure adapter is exercised by the local regression tests.
  if (typeof module !== 'undefined' && module.exports) { module.exports = { prepare, baseVariant }; return; }

  function initialize() {
    const node = document.querySelector('[data-np-gift-config]');
    const card = document.querySelector('[data-np-gift-box]');
    if (!node || !card || node.dataset.bound) return;
    const config = JSON.parse(node.textContent);
    node.dataset.bound = 'true';
    const check = card.querySelector('[data-gift-check]');
    const number = card.querySelector('[data-gift-quantity]');
    const minus = card.querySelector('[data-gift-minus]');
    const status = card.querySelector('[data-gift-status]');
    const section = document.querySelector('[id^="MainProduct-"]');
    const form = () => section?.querySelector('product-form.main-product-form form[action*="/cart/add"]');
    const selection = () => ({ checked: check.checked, quantity: Math.min(99, Math.max(1, Math.round(Number(number.value) || 1))) });
    function sync() {
      const quantity = selection().quantity;
      number.value = String(quantity);
      minus.disabled = quantity <= 1;
      card.querySelector('[data-gift-plus]').disabled = quantity >= 99;
      const message = check.checked ? `${quantity} gift ${quantity === 1 ? 'box' : 'boxes'} will be added to your bag.` : 'Optional. Only added when selected.';
      if (status.textContent !== message) status.textContent = message;
      document.dispatchEvent(new Event('np:gift-box-change'));
    }
    check.addEventListener('change', sync);
    number.addEventListener('change', sync);
    number.addEventListener('input', () => { if (number.value && number.validity.valid) sync(); });
    for (const [selector, amount] of [['[data-gift-minus]', -1], ['[data-gift-plus]', 1]]) {
      card.querySelector(selector).addEventListener('click', () => { number.value = String(selection().quantity + amount); sync(); });
    }
    function position() {
      // This campaign uses Teeinblue's own purchase controls and pack variants.
      const nativeActions = Number(config.productId) === 15446201729323 ? section?.querySelector('#teeFormActions') : null;
      if (nativeActions) {
        // Teeinblue hides generic [data-price] nodes; the card price is in config.
        card.removeAttribute('data-price');
        if (card.nextElementSibling !== nativeActions) nativeActions.before(card);
        card.hidden = false;
        return;
      }
      const bundle = document.querySelector('[data-npu-bundle]');
      if (bundle && bundle.parentElement === form()?.querySelector('.product-form__quantity-and-btn')) {
        if (card.nextElementSibling !== bundle) bundle.before(card);
        card.hidden = false;
      } else {
        const button = form()?.querySelector('button[name="add"]');
        if (button) { const anchor = button.closest('.np-bakven-purchase-row') || button; if (card.nextElementSibling !== anchor && !bundle) anchor.before(card); card.hidden = false; }
      }
    }
    function normalizePicker() {
      for (const wrapper of section?.querySelectorAll('variant-selects .np-legacy-gift-option') || []) {
        const radio = wrapper.querySelector('input[type="radio"][value="No Gift Box"]');
        const select = wrapper.querySelector('select');
        if (radio && !radio.checked) radio.click();
        if (select && select.value !== 'No Gift Box') { select.value = 'No Gift Box'; select.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      const input = form()?.querySelector('input[name="id"]');
      if (input?.value) {
        const id = baseVariant(input.value, config.variants);
        if (String(id) !== input.value) { input.value = String(id); input.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      const url = new URL(location.href);
      if (url.searchParams.has('variant')) {
        const oldId = url.searchParams.get('variant'); const id = baseVariant(oldId, config.variants);
        if (String(id) !== oldId) { url.searchParams.set('variant', String(id)); history.replaceState(history.state, '', url.href); }
      }
    }
    // Extend the request only for this product. The app continues to validate and create its customization.
    // Shopify's multi-item response is normalized for callers expecting the original single-item response.
    let customizationEdit = false;
    document.addEventListener('click', event => {
      const button = event.target.closest?.('#teeAtcButton');
      if (button) customizationEdit = !!button.closest('.vm--modal') && button.textContent.trim() === 'Update cart';
    }, true);
    const request = () => ({ ...selection(), editing: customizationEdit && !!document.querySelector('.vm--modal #teeAtcButton') });
    const originalFetch = window.fetch;
    window.fetch = async function(input, init) {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?\/?$/.test(url.pathname) || String(init?.method || input?.method || 'GET').toUpperCase() !== 'POST') return originalFetch.apply(this, arguments);
      let options = init;
      let requestBody = init?.body;
      if (!requestBody && input instanceof Request) {
        const type = input.headers.get('Content-Type') || '';
        requestBody = type.includes('multipart/form-data') ? await input.clone().formData() : type.includes('application/x-www-form-urlencoded') ? new URLSearchParams(await input.clone().text()) : await input.clone().text();
      }
      const result = prepare(requestBody, config, request());
      if (!result.changed) return originalFetch.apply(this, arguments);
      options = { ...(init || {}), body: result.body };
      if (input instanceof Request) { const headers = new Headers(init?.headers || input.headers); if (result.body instanceof FormData) headers.delete('Content-Type'); options.headers = headers; }
      const response = await originalFetch.call(this, input, options);
      if (!response.ok || !result.single) return response;
      const data = await response.clone().json();
      if (!Array.isArray(data.items)) return response;
      const main = data.items.find(item => config.variants.some(v => String(v.id) === String(item.variant_id || item.id)));
      if (!main) return response;
      const headers = new Headers(response.headers);
      headers.delete('Content-Length'); headers.delete('Content-Encoding');
      return new Response(JSON.stringify({ ...main, ...(data.sections ? { sections: data.sections } : {}) }), { status: response.status, statusText: response.statusText, headers });
    };
    // Some personalized forms are posted by the browser itself (the page goes straight to /cart), so fetch never sees
    // them. Just before the browser reads the fields, swap them for the items[] request prepare() builds, then put the
    // form back. The entry list is read synchronously, so the original fields return before anything else runs.
    function rewriteNative(target) {
      if (!target || target !== form()) return null;
      const result = prepare(new FormData(target), config, request());
      if (!result.changed) return null;
      const disabled = [...target.elements].filter(el => el.name && !el.disabled && el.type !== 'file' && !['submit', 'button'].includes(el.type));
      disabled.forEach(el => { el.disabled = true; });
      const added = [...result.body.entries()].filter(([, value]) => typeof value === 'string').map(([name, value]) => {
        const input = Object.assign(document.createElement('input'), { type: 'hidden', name, value });
        input.setAttribute('data-np-gift-native', '');
        return target.appendChild(input);
      });
      return () => { added.forEach(input => input.remove()); disabled.forEach(el => { el.disabled = false; }); };
    }
    const nativeSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function() {
      const restore = rewriteNative(this);
      try { return nativeSubmit.call(this); } finally { restore?.(); }
    };
    // requestSubmit() and a plain button press fire a submit event; only a submission nobody cancelled reaches the server
    // as a page load (the theme's own fetch path cancels it and is handled above).
    window.addEventListener('submit', event => {
      if (event.defaultPrevented) return;
      const restore = rewriteNative(event.target);
      if (restore) setTimeout(restore, 0);
    });
    const observer = new MutationObserver(() => { position(); normalizePicker(); });
    observer.observe(section || document.body, { childList: true, subtree: true });
    document.addEventListener('teeinblue-event-before-customization-created', normalizePicker);
    document.addEventListener('teeinblue-event-variant-changed', normalizePicker);
    window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
    normalizePicker(); position(); sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true }); else initialize();
})();
